import { formatDate } from '@rental/i18n'
import { Button, Field } from '@rental/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useRef } from 'react'
import { addDays } from '../board'
import { useLocale } from '../i18n'
import { SaidWords, useSaid } from './Said'
import { StaffInput } from './ui'

/**
 * A day to look from, and the way back and on (DESIGN.md, "A screen and its
 * parts"): before, the day itself, after, and today when it is not the day
 * shown. The screen keeps the day in its address, so Back and a link return
 * to it; this only asks for another. A step is the span the screen shows:
 * the Planning's seven days move by seven. The day a button leads to is said
 * as well as shown (UI-2g, as Today's arrows): the focus stays on the button,
 * and a field whose value changes under it is not read out.
 */
export function DateStepper({
  label,
  value,
  today,
  step,
  onChange,
  words,
}: {
  label: string
  /** The day shown, yyyy-mm-dd in Rome. */
  value: string
  today: string
  step: number
  onChange: (date: string) => void
  words: { earlier: string; later: string; today: string }
}) {
  const { locale } = useLocale()
  const field = useRef<HTMLInputElement>(null)
  const [said, say] = useSaid()
  const ask = (date: string) => {
    onChange(date)
    say(formatDate(locale, date))
  }
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Button
        variant="outline"
        className="rounded-sm px-3"
        aria-label={words.earlier}
        onClick={() => ask(addDays(value, -step))}
      >
        <ChevronLeft aria-hidden="true" className="size-6" />
      </Button>
      <Field label={label}>
        {(control) => (
          <StaffInput
            {...control}
            ref={field}
            type="date"
            value={value}
            // A field emptied with the keyboard is no day: the one shown stays.
            onChange={(event) => event.target.value && onChange(event.target.value)}
          />
        )}
      </Field>
      <Button
        variant="outline"
        className="rounded-sm px-3"
        aria-label={words.later}
        onClick={() => ask(addDays(value, step))}
      >
        <ChevronRight aria-hidden="true" className="size-6" />
      </Button>
      {value !== today ? (
        <Button
          variant="outline"
          className="rounded-sm"
          onClick={() => {
            // The button goes once today is shown: the focus goes to the day, not to nothing.
            field.current?.focus()
            ask(today)
          }}
        >
          {words.today}
        </Button>
      ) : null}
      <p role="status" className="sr-only">
        <SaidWords said={said} />
      </p>
    </div>
  )
}
